export const openCenterSectionToPath = {
  openOverview: 'overview',
  serverConnection: 'server-connection',
  skillRegistration: 'skill-registration',
  protocolReference: 'protocol-reference'
}

export const openCenterContent = {
  defaultSection: 'openOverview',
  pageTitle: '开放中心',
  pageSubtitle:
    '面向接入方的协议文档。无论使用什么语言或运行环境，只需按统一消息结构完成连接、注册和调用，即可将能力接入平台。',
  menuTitle: '开放中心导航',
  searchPlaceholder:
    '搜索 task_id、skill_register、request_id、requestId、127.0.0.1、skill_result...',
  searchEmpty: '没有找到匹配内容，请尝试更具体的协议关键字。',
  comingSoon: '开放中心文档正在整理中。',
  copyButtonLabel: '复制当前文档',
  copySuccessLabel: '已复制',
  copyErrorLabel: '复制失败',
  copyButtonHint:
    '复制当前文档全文，便于直接交给 AI 生成接入代码、联调脚本或测试样例。',
  usageTipTitle: '接入提示',
  usageTipContent:
    '开放中心文档以协议为准，不限定具体开发语言。接入时请优先保证字段名称、标识关联关系、消息方向和返回约束一致。',
  categories: [
    {
      id: 'gettingStarted',
      title: '接入总览',
      items: [
        {
          id: 'openOverview',
          name: '接入说明',
          desc: '理解开放中心定位、标准接入流程，以及任务与实例标识在平台中的作用'
        },
        {
          id: 'serverConnection',
          name: '连接 Server',
          desc: '默认地址、欢迎消息、注册时限、节点注册格式与心跳要求'
        }
      ]
    },
    {
      id: 'skillDevelopment',
      title: '技能注册',
      items: [
        {
          id: 'skillRegistration',
          name: '注册协议',
          desc: '技能元数据、参数结构、字段说明，以及 skill_register 注册消息'
        }
      ]
    },
    {
      id: 'protocolSpecs',
      title: '协议参考',
      items: [
        {
          id: 'protocolReference',
          name: '消息协议',
          desc: 'skill_invoke、skill_result、skill_start、skill_push、agent_generate 等消息说明'
        }
      ]
    }
  ],
  content: {
    openOverview: {
      title: '接入说明',
      summary:
        '开放中心为具备开发能力的合作方提供统一接入入口。平台关心的是协议是否符合规范，而不是接入方采用什么语言、框架或部署方式。',
      sections: [
        {
          subtitle: '适用对象',
          bullets: [
            '希望将现有服务、工具、自动化流程或内部能力接入平台的团队。',
            '希望将业务能力封装为平台可调用技能的合作方。',
            '需要围绕统一协议开展联调、测试、运维和交付的技术团队。'
          ]
        },
        {
          subtitle: '接入原则',
          cards: [
            {
              title: '协议优先',
              desc: '平台以消息结构和字段语义识别能力，不要求绑定某一种开发语言或 SDK。'
            },
            {
              title: '本地直连',
              desc: '默认接入入口为本地 Server。联调阶段建议先完成本地协议闭环，再扩展到其他部署形态。'
            },
            {
              title: '标识稳定',
              desc: 'task_id、instance_id、request_id 等标识需要贯穿消息链路，不能随意省略或临时变更。'
            }
          ]
        },
        {
          subtitle: '标准接入流程',
          steps: [
            '与本地 Server 建立 WebSocket 连接。',
            '收到 welcome 消息后，在规定时间内发送 register 完成节点注册。',
            '使用注册成功返回的 client_id 作为 node_id，发送 skill_register 注册技能列表。',
            '根据 execution_mode 接收 skill_start 或 skill_invoke，并按对应协议回传状态、推送或结果。',
            '通过 heartbeat、skill_status、skill_push 等消息维持运行状态同步。'
          ]
        },
        {
          subtitle: '关键标识说明',
          markdown: `| 标识 | 产生方 | 作用 | 接入要求 |
| --- | --- | --- | --- |
| \`node_id\` | Server 在注册成功后分配 | 标识当前接入节点 | 后续 \`skill_register\`、心跳和相关消息应与该节点保持一致 |
| \`task_id\` | Server 在任务调度时分配 | 标识一次真实任务上下文 | 对 \`skill_start\`、\`skill_invoke\`、\`skill_push\`、\`skill_result\` 非常重要，建议完整透传 |
| \`instance_id\` | Server 在技能下发前生成 | 标识技能实例 | 后续状态变更、停止和回传需持续使用同一个实例 ID |
| \`request_id\` | 调用链生成 | 关联阻塞调用、Agent 请求和服务器信息请求 | \`skill_result\`、\`agent_reply\`、\`server_info_reply\` 必须准确回填 |
| \`requestId\` | 注册与通用响应层使用 | 关联 \`register\` / \`response\` 这类顶层请求响应 | 与 \`request_id\` 并存，命名大小写不能混用 |`
        },
        {
          subtitle: '重要说明',
          callout: {
            tone: 'warning',
            title: '`task_id` 不是展示字段，而是调度主键',
            content:
              '平台会使用 task_id 进行任务绑定、上下文恢复、推送归档、结果同步和部分业务联动。接入方如果省略 task_id，虽然个别场景可能还能靠 instance_id 回补，但会直接增加联调和运行风险。'
          }
        }
      ]
    },
    serverConnection: {
      title: '连接 Server',
      summary:
        '当前默认接入方式为连接本地 Server。建立 WebSocket 连接后，需要先完成节点注册，再进入技能注册与消息交互阶段。',
      sections: [
        {
          subtitle: '默认连接参数',
          cards: [
            { title: 'Host', desc: '`127.0.0.1`' },
            { title: 'Port', desc: '`8765`' },
            { title: 'Protocol', desc: '`WebSocket`' },
            { title: 'URL', desc: '`ws://127.0.0.1:8765`' }
          ]
        },
        {
          subtitle: '建立连接后会收到什么',
          markdown: `连接建立成功后，Server 会先发送一条欢迎消息，并要求接入方在限定时间内完成注册：

\`\`\`json
{
  "type": "system",
  "action": "welcome",
  "status": "ok",
  "message": "请在10秒内发送注册消息",
  "data": {
    "server_version": "当前服务端版本",
    "protocol_version": "1.0",
    "registration_required": true,
    "registration_timeout": 10
  }
}
\`\`\`

当前实现中，注册超时时间为 **10 秒**。超时未注册的连接会被关闭。`
        },
        {
          subtitle: '节点注册消息',
          markdown: `接入方应在 welcome 之后立即发送 \`register\`：

\`\`\`json
{
  "type": "register",
  "client_type": "node",
  "data": {
    "version": "1.0.0",
    "max_concurrent": 5,
    "metadata": {
      "platform": "windows",
      "env": "local"
    }
  },
  "requestId": 1
}
\`\`\`

字段说明：

| 字段 | 必填 | 说明 |
| --- | --- | --- |
| \`type\` | 是 | 固定为 \`register\` |
| \`client_type\` | 是 | 当前节点接入固定为 \`node\` |
| \`data.version\` | 是 | 当前接入端版本号 |
| \`data.max_concurrent\` | 是 | 节点可承载的并发数 |
| \`data.metadata\` | 否 | 节点元数据，可补充平台、环境、能力说明等信息 |
| \`requestId\` | 是 | 注册请求编号，用于匹配响应 |`
        },
        {
          subtitle: '注册成功响应',
          markdown: `注册成功后，Server 返回：

\`\`\`json
{
  "status": "ok",
  "message": "Registration successful",
  "data": {
    "client_id": "node_1234567890",
    "server_version": "当前服务端版本",
    "protocol_version": "1.0",
    "assigned_pool": "node_clients",
    "registry_stats": {
      "total_nodes": 1
    }
  },
  "requestId": 1
}
\`\`\`

其中 \`data.client_id\` 就是后续要使用的 \`node_id\`。建议接入方不要自行发明另一套节点标识。`
        },
        {
          subtitle: '命名差异',
          markdown: `当前协议存在两套相关但不同的关联字段：

| 字段 | 使用范围 |
| --- | --- |
| \`requestId\` | 节点注册、顶层 response、通用命令响应 |
| \`request_id\` | 技能阻塞调用、Agent 请求、服务器信息请求等技能协议层消息 |

当前服务端实现能够兼容读取 \`requestId\` 或 \`request_id\`，但接入文档建议按真实消息类型分别使用，避免联调时出现字段命名混乱。`
        },
        {
          subtitle: '心跳消息',
          markdown: `注册完成后，节点应定期发送心跳：

\`\`\`json
{
  "type": "heartbeat",
  "node_id": "node_1234567890",
  "status": {
    "running_skills": 2,
    "uptime": null
  },
  "timestamp": "2026-04-08T12:00:00"
}
\`\`\`

服务端当前主要通过心跳更新节点存活状态。建议至少保持 \`node_id\` 正确，其他状态字段可按实际能力补充。`
        },
        {
          subtitle: '连接建议',
          callout: {
            tone: 'info',
            title: '先跑通本地链路',
            content:
              '默认联调入口就是 ws://127.0.0.1:8765。建议优先验证本地连接、注册、技能调用和回传是否闭环，再考虑远程地址、代理转发或跨机部署。'
          }
        }
      ]
    },
    skillRegistration: {
      title: '注册协议',
      summary:
        '技能注册的核心不是实现方式，而是元数据是否完整、字段是否稳定、参数是否可被平台正确理解和调度。',
      sections: [
        {
          subtitle: '技能元数据字段',
          markdown: `每个技能应按照统一结构声明：

| 字段 | 必填 | 说明 |
| --- | --- | --- |
| \`skill_id\` | 是 | 技能唯一标识，建议稳定、可读、可长期复用 |
| \`name\` | 是 | 技能展示名称 |
| \`description\` | 是 | 技能用途说明，建议直接描述解决什么问题 |
| \`version\` | 是 | 技能版本号 |
| \`parameters\` | 否 | 参数列表 |
| \`execution_mode\` | 是 | 执行模式，当前为 \`blocking\` 或 \`background\` |
| \`long_running\` | 否 | 是否为长期运行型技能 |
| \`push_enabled\` | 否 | 是否支持主动推送 |
| \`category\` | 否 | 技能分类，例如 \`general\`、\`automation\`、\`monitor\` |
| \`tags\` | 否 | 标签列表，用于检索与归类 |`
        },
        {
          subtitle: '参数结构',
          markdown: `\`parameters\` 为对象数组，每个参数支持以下字段：

| 字段 | 必填 | 说明 |
| --- | --- | --- |
| \`name\` | 是 | 参数名 |
| \`type\` | 是 | 参数类型 |
| \`required\` | 是 | 是否必填 |
| \`description\` | 是 | 参数说明 |
| \`default\` | 否 | 默认值 |
| \`enum\` | 否 | 枚举值列表 |

当前已支持的参数类型：

| 类型 | 说明 |
| --- | --- |
| \`string\` | 字符串 |
| \`int\` | 整数 |
| \`float\` | 浮点数 |
| \`bool\` | 布尔值 |
| \`array\` | 数组 |
| \`object\` | 对象 |`
        },
        {
          subtitle: '技能注册消息',
          markdown: `技能列表通过 \`skill_register\` 提交：

\`\`\`json
{
  "type": "skill_register",
  "node_id": "node_1234567890",
  "skills": [
    {
      "skill_id": "news_monitor",
      "name": "新闻监测",
      "description": "根据关键词监测并推送新闻更新",
      "version": "1.0.0",
      "parameters": [
        {
          "name": "keywords",
          "type": "array",
          "required": true,
          "description": "需要监测的关键词列表"
        },
        {
          "name": "source",
          "type": "string",
          "required": false,
          "description": "监测来源",
          "default": "all"
        }
      ],
      "execution_mode": "background",
      "long_running": true,
      "push_enabled": true,
      "category": "monitor",
      "tags": ["news", "monitor"]
    }
  ],
  "timestamp": "2026-04-08T12:00:00"
}
\`\`\`

字段说明：

| 字段 | 说明 |
| --- | --- |
| \`node_id\` | 当前节点标识，应使用 register 成功后返回的 \`client_id\` |
| \`skills\` | 当前节点可提供的技能列表 |
| \`timestamp\` | 注册时间，建议带上 |`
        },
        {
          subtitle: '执行模式约束',
          markdown: `| execution_mode | 平台下发消息 | 接入方需要返回什么 |
| --- | --- | --- |
| \`blocking\` | \`skill_invoke\` | 最终必须返回 \`skill_result\` |
| \`background\` | \`skill_start\` / \`skill_stop\` | 运行中通过 \`skill_status\`、\`skill_push\` 回传状态与结果 |`
        },
        {
          subtitle: '注册建议',
          bullets: [
            '`skill_id` 建议使用长期稳定命名，不要把临时活动名、版本号或环境名直接写进 ID。',
            '`description` 建议描述结果价值，不要写成内部实现说明。',
            '参数定义要尽量完整，尤其是 required、description、enum，这些信息会直接影响后续调用质量。',
            '如果技能需要持续输出中间结果，建议明确设置为 background，并打开 push_enabled。'
          ]
        },
        {
          subtitle: '注意事项',
          callout: {
            tone: 'warning',
            title: '执行模式会直接决定消息流',
            content:
              '如果将技能注册为 blocking，就必须支持 skill_invoke -> skill_result 的闭环；如果注册为 background，就应支持 skill_start -> skill_status / skill_push -> skill_stop。执行模式与消息行为不一致，平台无法正确判断技能状态。'
          }
        }
      ]
    },
    protocolReference: {
      title: '消息协议',
      summary:
        '以下内容按当前真实消息流整理，重点说明平台实际下发和实际消费的字段，而不是抽象协议草案。',
      sections: [
        {
          subtitle: '消息类型总览',
          markdown: `| 类型 | 方向 | 用途 |
| --- | --- | --- |
| \`register\` | Node -> Server | 节点注册 |
| \`skill_register\` | Node -> Server | 技能注册 |
| \`heartbeat\` | Node -> Server | 心跳保活 |
| \`skill_start\` | Server -> Node | 启动后台技能 |
| \`skill_invoke\` | Server -> Node | 发起阻塞技能调用 |
| \`skill_stop\` | Server -> Node | 停止后台技能 |
| \`skill_push\` | Node -> Server | 推送中间结果、最终结果、进度或错误 |
| \`skill_status\` | Node -> Server | 上报后台技能状态 |
| \`skill_result\` | Node -> Server | 返回阻塞技能最终结果 |
| \`agent_generate\` | Node -> Server | 请求平台模型生成内容 |
| \`agent_reply\` | Server -> Node | 返回平台模型处理结果 |
| \`get_server_info\` | Node -> Server | 查询服务端信息 |
| \`server_info_reply\` | Server -> Node | 返回服务端信息 |
| \`response\` | 双向通用 | 顶层请求响应 |`
        },
        {
          subtitle: '阻塞技能调用',
          markdown: `适用于必须拿到最终结果才能继续后续流程的能力。

Server 下发：

\`\`\`json
{
  "type": "skill_invoke",
  "request_id": "invoke_7d0d4c",
  "task_id": "task_20260408_001",
  "skill_id": "code_review",
  "instance_id": "code_review_1712563200_ab12cd",
  "parameters": {
    "target": "README.md"
  }
}
\`\`\`

Node 返回：

\`\`\`json
{
  "type": "skill_result",
  "request_id": "invoke_7d0d4c",
  "instance_id": "code_review_1712563200_ab12cd",
  "skill_id": "code_review",
  "task_id": "task_20260408_001",
  "status": "ok",
  "result": {
    "summary": "检查完成"
  }
}
\`\`\`

错误场景：

\`\`\`json
{
  "type": "skill_result",
  "request_id": "invoke_7d0d4c",
  "instance_id": "code_review_1712563200_ab12cd",
  "skill_id": "code_review",
  "task_id": "task_20260408_001",
  "status": "error",
  "error": "参数不合法"
}
\`\`\`

约束说明：

1. \`request_id\` 是阻塞调用结果匹配键，缺失时服务端会直接忽略该 \`skill_result\`。
2. \`task_id\` 建议始终原样回传；当前服务端虽然可在部分场景通过 \`instance_id\` 补回，但不应依赖这种兜底。
3. 当前服务端真实下发的 \`skill_invoke\` 中不包含 \`timeout_ms\`；如需超时控制，应视为扩展字段处理，而不是当前接入前置条件。`
        },
        {
          subtitle: '后台技能调用',
          markdown: `适用于持续运行、异步回传的能力。

Server 启动后台技能：

\`\`\`json
{
  "type": "skill_start",
  "task_id": "task_20260408_002",
  "skill_id": "news_monitor",
  "instance_id": "news_monitor_1712563201_f3e9ab",
  "parameters": {
    "keywords": ["AI", "模型"]
  }
}
\`\`\`

Server 停止后台技能：

\`\`\`json
{
  "type": "skill_stop",
  "instance_id": "news_monitor_1712563201_f3e9ab",
  "reason": "User request"
}
\`\`\`

说明：

| 字段 | 说明 |
| --- | --- |
| \`task_id\` | 后台任务上下文标识，启动消息中会下发 |
| \`instance_id\` | 后台技能实例句柄，停止、状态同步、推送都要依赖它 |
| \`reason\` | 停止原因，当前为可选字段 |`
        },
        {
          subtitle: '状态与推送',
          markdown: `后台技能运行期间，通常会用到两类回传消息。

状态上报：

\`\`\`json
{
  "type": "skill_status",
  "instance_id": "news_monitor_1712563201_f3e9ab",
  "skill_id": "news_monitor",
  "status": "running",
  "message": "监测中"
}
\`\`\`

结果或中间数据推送：

\`\`\`json
{
  "type": "skill_push",
  "instance_id": "news_monitor_1712563201_f3e9ab",
  "skill_id": "news_monitor",
  "task_id": "task_20260408_002",
  "data_type": "result",
  "response_mode": "reply_to_user",
  "data": {
    "metadata": {
      "skill_id": "news_monitor",
      "skill_name": "新闻监测",
      "push_type": "result"
    },
    "content": "今日共发现 3 条与 AI 相关的更新。"
  }
}
\`\`\`

字段约束：

| 字段 | 说明 |
| --- | --- |
| \`data_type\` | 当前支持 \`update\`、\`result\`、\`error\`、\`progress\` |
| \`response_mode\` | 当前支持 \`no_reply\`、\`reply_to_node\`、\`reply_to_user\` |
| \`task_id\` | \`skill_push\` 强烈建议携带，服务端会用于任务绑定、上下文记录和推送归档 |
| \`instance_id\` | \`skill_status\` 不带 \`task_id\`，服务端会依赖 \`instance_id\` 反查所属任务 |
| \`request_id\` | 当 \`response_mode = reply_to_node\` 时建议携带；需要平台回调时应作为关联键 |

\`reply_to_user\` 的额外要求：

1. \`data\` 应为对象。
2. 对象中应包含 \`metadata\` 与 \`content\`。
3. 若同时带有 \`request_id\`，服务端会优先以带 \`requestId\` 的最终响应形式返回；否则会退化为广播消息。`
        },
        {
          subtitle: 'Agent 生成协议',
          markdown: `当技能需要借助平台模型生成内容时，可发送 \`agent_generate\`。

当前实现说明：**不支持通过接入协议自定义切换模型**。虽然消息中保留了 \`model_id\` 字段，但服务端当前会统一按平台基础模型处理，接入方不应将 \`model_id\` 视为可生效的模型选择开关。

\`\`\`json
{
  "type": "agent_generate",
  "request_id": "agent_req_001",
  "prompt": "请根据以下监测结果整理成一段摘要",
  "system_prompt": "你是一个信息整理助手",
  "model_id": "base-model-id",
  "context": {
    "instance_id": "news_monitor_1712563201_f3e9ab",
    "source": "news_monitor",
    "task_id": "task_20260408_002"
  }
}
\`\`\`

平台返回：

\`\`\`json
{
  "type": "agent_reply",
  "request_id": "agent_req_001",
  "instance_id": "news_monitor_1712563201_f3e9ab",
  "skill_id": "news_monitor",
  "status": "ok",
  "data": {
    "generated_content": "已整理完成的摘要内容",
    "model_used": "BaseModel",
    "timestamp": "2026-04-08T12:00:00"
  }
}
\`\`\`

说明：

| 字段 | 说明 |
| --- | --- |
| \`context.instance_id\` | 建议带上，便于平台回传到对应技能实例 |
| \`context.source\` | 当前服务端会将其作为 \`skill_id\` 的回退来源 |
| \`context.task_id\` | 建议带上，用于任务上下文关联 |
| \`model_id\` | 协议层保留字段；当前实现不会按该字段切换模型，服务端统一按平台基础模型处理 |`
        },
        {
          subtitle: '查询服务端信息',
          markdown: `Node 侧可以主动查询服务端基础信息：

\`\`\`json
{
  "type": "get_server_info",
  "request_id": "server_info_001",
  "info_type": "all",
  "context": {
    "instance_id": "news_monitor_1712563201_f3e9ab"
  }
}
\`\`\`

Server 返回：

\`\`\`json
{
  "type": "server_info_reply",
  "request_id": "server_info_001",
  "instance_id": "news_monitor_1712563201_f3e9ab",
  "status": "ok",
  "data": {
    "install_path": "服务端安装目录",
    "tools_path": "服务端 data 目录",
    "version": "当前服务端版本",
    "platform": "Windows"
  }
}
\`\`\`

\`info_type\` 当前可请求 \`all\`，也可请求单项信息，例如 \`install_path\`、\`tools_path\`、\`version\`。`
        },
        {
          subtitle: '联调时最容易遗漏的点',
          callout: {
            tone: 'warning',
            title: '优先检查四件事',
            content:
              '第一，register 和 response 层用的是 requestId；第二，技能协议层主要用 request_id；第三，blocking 技能没有 request_id 就无法正确回结果；第四，background 技能的 skill_status 不带 task_id，因此 instance_id 必须保持稳定。'
          }
        }
      ]
    }
  }
}
